import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User, Session } from "@supabase/supabase-js";
import { toast } from "sonner";
import { UserProfile, UserRole, getUserPermissions, hasPermission, isVendedorScopedRole, isVendedorExternoRole, isVendedorInternoRole } from "@/types/auth";

type AuthContextValue = {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  loading: boolean;
  profileError: string | null;
  signIn: (email: string, password: string) => Promise<{ data?: unknown; error?: unknown }>;
  signUp: (email: string, password: string, userData: { nome: string; telefone?: string }) => Promise<{ data?: unknown; error?: unknown }>;
  signOut: () => Promise<{ data?: null; error?: unknown }>;
  refreshProfile: () => Promise<void>;
  isTi: () => boolean;
  isAdmin: () => boolean;
  isOperator: () => boolean;
  isViewer: () => boolean;
  /** Alias de Vendedor Externo (role `vendedor`) — mantido por compatibilidade */
  isVendedor: () => boolean;
  isVendedorExterno: () => boolean;
  isVendedorInterno: () => boolean;
  /** Externo ou interno — isolamento por vendedor_id */
  isVendedorScoped: () => boolean;
  hasUserPermission: (requiredRole: UserRole) => boolean;
  getUserPermissionsData: () => ReturnType<typeof getUserPermissions> | null;
  canView: () => boolean;
  canEdit: () => boolean;
  canDelete: () => boolean;
  canManageUsers: () => boolean;
  canManageSettings: () => boolean;
  canManageFinance: () => boolean;
  canAccessCadastros: () => boolean;
  canAccessServicos: () => boolean;
  canAccessEquipamentos: () => boolean;
  canAccessFormasPagamento: () => boolean;
  canAccessUsuarios: () => boolean;
  canAccessFinanceiro: () => boolean;
  canAccessAgenda: () => boolean;
  /** Operador não vê contas a pagar nem fluxo de caixa; admin/ti/viewer veem */
  canAccessContasAPagar: () => boolean;
  canAccessProjetos: () => boolean;
  canAccessContratos: () => boolean;
  canAccessOrcamentos: () => boolean;
  canAccessSinistros: () => boolean;
  canAccessPedidos: () => boolean;
  canAccessCrm: () => boolean;
  canAccessTarefas: () => boolean;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileError, setProfileError] = useState<string | null>(null);
  const initialLoadDoneRef = useRef(false);

  const buildFallbackProfile = useCallback((u: User): UserProfile => {
    const role =
      u.email === "suporte.ti@sunpro.srv.br"
        ? "ti"
        : ((u.user_metadata?.role as UserProfile["role"]) || "viewer");
    return {
      id: u.id,
      email: u.email || "",
      nome: (u.user_metadata?.nome as string) || u.email?.split("@")[0] || "Usuário",
      role,
      user_role: role,
      ativo: true,
      created_at: new Date().toISOString(),
    };
  }, []);

  const fetchUserProfile = useCallback(async (userId: string, retryCount = 0): Promise<boolean> => {
    const maxRetries = 2;
    setProfileError(null);
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .single();

      if (error) {
        const msg = error.message || `${error.code || "erro"}`;
        setProfileError(msg);
        if (error.code === "PGRST116") {
          const { data: userData } = await supabase.auth.getUser();
          if (userData.user) {
            const { error: insertErr } = await supabase.from("profiles").insert({
              id: userId,
              email: userData.user.email || "",
              nome: (userData.user.user_metadata?.nome as string) || "Usuário",
              user_role: "operator",
              role: "operator",
              ativo: true,
            });
            if (insertErr) {
              setProfile(buildFallbackProfile(userData.user));
              setProfileError(null);
              return true;
            }
            const { data: newProfile } = await supabase
              .from("profiles")
              .select("*")
              .eq("id", userId)
              .single();
            if (newProfile) {
              setProfile(newProfile as UserProfile);
              setProfileError(null);
              return true;
            }
          }
          const { data: fallbackUser } = await supabase.auth.getUser();
          if (fallbackUser.user) {
            setProfile(buildFallbackProfile(fallbackUser.user));
            setProfileError(null);
            return true;
          }
        } else {
          const { data: fallbackUser } = await supabase.auth.getUser();
          if (fallbackUser.user) {
            setProfile(buildFallbackProfile(fallbackUser.user));
            setProfileError(null);
            return true;
          }
        }
        return false;
      }
      if (data) {
        const profileData = data as UserProfile;
        if (profileData.role == null || profileData.role === undefined) {
          setProfile({ ...profileData, role: (profileData.user_role as UserProfile["role"]) || "viewer", user_role: (profileData.user_role as UserProfile["role"]) || "viewer" });
        } else {
          setProfile(profileData);
        }
        setProfileError(null);
        return true;
      }
      setProfileError("Perfil não encontrado");
      const { data: fallbackUser } = await supabase.auth.getUser();
      if (fallbackUser.user) {
        setProfile(buildFallbackProfile(fallbackUser.user));
        setProfileError(null);
        return true;
      }
      return false;
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Erro ao buscar perfil";
      setProfileError(msg);
      console.error("Erro ao buscar perfil:", err);
      if (retryCount < maxRetries) {
        await new Promise((r) => setTimeout(r, 500));
        return fetchUserProfile(userId, retryCount + 1);
      }
      const { data: fallbackUser } = await supabase.auth.getUser();
      if (fallbackUser.user) {
        setProfile(buildFallbackProfile(fallbackUser.user));
        setProfileError(null);
        return true;
      }
      return false;
    }
  }, [buildFallbackProfile]);

  useEffect(() => {
    let cancelled = false;
    const LOADING_TIMEOUT_MS = 5000;

    const ensureProfileThenFinish = async (sessionUser: User) => {
      try {
        // Não deixar travar: se o Supabase demorar/falhar, usamos fallback local (sem rede)
        const ok = await Promise.race<boolean>([
          fetchUserProfile(sessionUser.id),
          new Promise<boolean>((resolve) => window.setTimeout(() => resolve(false), 2500)),
        ]);
        if (!cancelled) {
          if (!ok) {
            setProfile(buildFallbackProfile(sessionUser));
            setProfileError(null);
          }
          setLoading(false);
          initialLoadDoneRef.current = true;
        }
      } catch {
        if (!cancelled) {
          setProfile(buildFallbackProfile(sessionUser));
          setProfileError(null);
          setLoading(false);
          initialLoadDoneRef.current = true;
        }
      }
    };

    const timeoutId = window.setTimeout(() => {
      if (!cancelled) {
        // Se estourar o timeout e ainda não tiver profile, libera com fallback local
        if (user && !profile) {
          setProfile(buildFallbackProfile(user));
          setProfileError(null);
          initialLoadDoneRef.current = true;
        }
        setLoading(false);
      }
    }, LOADING_TIMEOUT_MS);

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        if (cancelled) return;
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          if (initialLoadDoneRef.current) {
            fetchUserProfile(session.user.id).catch(() => {});
          } else {
            setLoading(true);
            await ensureProfileThenFinish(session.user);
          }
        } else {
          initialLoadDoneRef.current = false;
          setProfile(null);
          setProfileError(null);
          setLoading(false);
        }
      }
    );

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (cancelled) return;
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        await ensureProfileThenFinish(session.user);
      } else {
        setProfile(null);
        setProfileError(null);
        if (!cancelled) setLoading(false);
      }
    }).catch(() => {
      if (!cancelled) setLoading(false);
    });

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, [fetchUserProfile]);

  const refreshProfile = useCallback(async () => {
    if (user?.id) await fetchUserProfile(user.id);
  }, [user?.id, fetchUserProfile]);

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      const { error, data } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        toast.error("Erro ao fazer login: " + error.message);
        return { error };
      }
      toast.success("Login realizado com sucesso!");
      return { data };
    } catch (err) {
      toast.error("Erro inesperado no login");
      return { error: err };
    }
  }, []);

  const signUp = useCallback(async (
    email: string,
    password: string,
    userData: { nome: string; telefone?: string }
  ) => {
    try {
      const { error, data } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { nome: userData.nome, telefone: userData.telefone } },
      });
      if (error) {
        toast.error("Erro ao criar conta: " + error.message);
        return { error };
      }
      toast.success("Conta criada com sucesso! Verifique seu email para confirmar.");
      return { data };
    } catch (err) {
      toast.error("Erro inesperado no cadastro");
      return { error: err };
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        toast.error("Erro ao fazer logout: " + error.message);
        return { error };
      }
      toast.success("Logout realizado com sucesso!");
      return { data: null };
    } catch (err) {
      toast.error("Erro inesperado no logout");
      return { error: err };
    }
  }, []);

  const normalizeRole = useCallback((role: unknown): UserRole | null => {
    if (
      role === "admin" ||
      role === "operator" ||
      role === "viewer" ||
      role === "ti" ||
      role === "vendedor" ||
      role === "vendedor_interno"
    ) {
      return role;
    }
    return null;
  }, []);

  const effectiveRole: UserRole | null =
    user?.email === "suporte.ti@sunpro.srv.br"
      ? "ti"
      : normalizeRole(profile?.role) ?? normalizeRole((user?.user_metadata as any)?.role);

  const isTi = useCallback(() => {
    return effectiveRole === "ti";
  }, [effectiveRole]);

  const isAdmin = () => effectiveRole === "admin" || isTi();
  const isOperator = () => effectiveRole === "operator" || effectiveRole === "admin" || isTi();
  /** Vendedor Externo (role `vendedor`) */
  const isVendedorExterno = () => isVendedorExternoRole(effectiveRole);
  const isVendedorInterno = () => isVendedorInternoRole(effectiveRole);
  const isVendedorScoped = () => isVendedorScopedRole(effectiveRole);
  /** Alias legado = Vendedor Externo */
  const isVendedor = () => isVendedorExterno();
  const isViewer = () =>
    effectiveRole === "viewer" ||
    effectiveRole === "operator" ||
    effectiveRole === "admin" ||
    isTi() ||
    isVendedorScoped();

  const hasUserPermission = useCallback(
    (requiredRole: UserRole) => {
      if (!effectiveRole) return false;
      return hasPermission(effectiveRole, requiredRole);
    },
    [effectiveRole]
  );

  const getUserPermissionsData = useCallback(() => {
    if (!effectiveRole) return null;
    return getUserPermissions(effectiveRole);
  }, [effectiveRole]);

  const canView = () => isViewer() || isTi();
  const canEdit = () => isOperator() || isTi();
  const canDelete = () => isAdmin() || isTi();
  const canManageUsers = () => isAdmin() || isTi();
  const canManageSettings = () => isAdmin() || isTi();
  const canManageFinance = () => isOperator() || isTi();
  const canAccessCadastros = () =>
    !isVendedorScoped() && (isAdmin() || effectiveRole === "operator" || isTi());
  const canAccessServicos = () => !isVendedorScoped() && (isAdmin() || isTi());
  const canAccessEquipamentos = () => !isVendedorScoped() && (canView() || isTi());
  const canAccessFormasPagamento = () => !isVendedorScoped() && (canView() || isTi());
  const canAccessUsuarios = () => isAdmin() || isTi();
  // Interno: contas a receber; Externo: sem financeiro
  const canAccessFinanceiro = () =>
    isVendedorInterno() || (!isVendedorScoped() && (canView() || isTi()));
  const canAccessContasAPagar = () =>
    !isVendedorScoped() && effectiveRole !== "operator";
  // Interno: agenda de renovações do próprio escopo; Externo: sem agenda
  const canAccessAgenda = () =>
    isVendedorInterno() || (!isVendedorScoped() && (canView() || isTi()));
  const canAccessProjetos = () => canView() || isTi();
  const canAccessContratos = () =>
    isVendedorInterno() || (!isVendedorScoped() && (canView() || isTi()));
  const canAccessOrcamentos = () => canView() || isTi();
  const canAccessSinistros = () => !isVendedorScoped() && (canView() || isTi());
  const canAccessPedidos = () =>
    isVendedorInterno() || (!isVendedorScoped() && (canView() || isTi()));
  const canAccessCrm = () => !isVendedorScoped() && (canView() || isTi());
  /** Todos os papéis autenticados (inclui vendedores) */
  const canAccessTarefas = () => canView() || isTi();

  const value: AuthContextValue = {
    user,
    session,
    profile,
    loading,
    profileError,
    signIn,
    signUp,
    signOut,
    refreshProfile,
    isTi,
    isAdmin,
    isOperator,
    isViewer,
    isVendedor,
    isVendedorExterno,
    isVendedorInterno,
    isVendedorScoped,
    hasUserPermission,
    getUserPermissionsData,
    canView,
    canEdit,
    canDelete,
    canManageUsers,
    canManageSettings,
    canManageFinance,
    canAccessCadastros,
    canAccessServicos,
    canAccessEquipamentos,
    canAccessFormasPagamento,
    canAccessUsuarios,
    canAccessFinanceiro,
    canAccessContasAPagar,
    canAccessAgenda,
    canAccessProjetos,
    canAccessContratos,
    canAccessOrcamentos,
    canAccessSinistros,
    canAccessPedidos,
    canAccessCrm,
    canAccessTarefas,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
